import { useCallback, useEffect, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Megaphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUiStore } from "@/stores/ui";
import { PANEL_PATH } from "@/admin/types";

const SHOW_DELAY_MS = 10_000;
const STORAGE_KEY = "eusebio.advertisePromptDismissedAt";
const SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000;

function isAdvertisePromptSuppressed(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < SUPPRESS_MS;
  } catch {
    return false;
  }
}

function markAdvertisePromptDismissed(): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

function isFormPath(pathname: string): boolean {
  return (
    pathname.startsWith("/anuncie") ||
    pathname.startsWith("/contato") ||
    pathname.startsWith("/entrar")
  );
}

function isFormField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest("form") || target.matches("input, textarea, select, [contenteditable='true']"),
  );
}

export function AdvertisePartnerPrompt() {
  const location = useLocation();
  const navigate = useNavigate();
  const cookieConsent = useUiStore((state) => state.cookieConsent);
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(() => isAdvertisePromptSuppressed());

  const onFormPage = isFormPath(location.pathname);
  const onPanel =
    location.pathname.startsWith(PANEL_PATH) || location.pathname.startsWith("/entrar");

  const handleDismiss = useCallback(() => {
    markAdvertisePromptDismissed();
    setDismissed(true);
    setReady(false);
  }, []);

  useEffect(() => {
    if (cookieConsent === "pending" || onFormPage || onPanel || dismissed) {
      setReady(false);
      return;
    }
    const timer = window.setTimeout(() => {
      if (isFormField(document.activeElement)) return;
      setReady(true);
    }, SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [cookieConsent, onFormPage, onPanel, dismissed, location.pathname]);

  const visible =
    ready && !dismissed && !onFormPage && !onPanel && cookieConsent !== "pending";

  useEffect(() => {
    if (!visible) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") handleDismiss();
    }

    function onFocusIn(event: FocusEvent) {
      if (!isFormField(event.target)) return;
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('[data-advertise-prompt="dialog"]')) {
        return;
      }
      handleDismiss();
    }

    window.addEventListener("keydown", onKey);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [visible, handleDismiss]);

  if (!visible || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-brand-ink/50 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center"
      role="presentation"
      style={{ cursor: "pointer", touchAction: "manipulation" }}
      onClick={(event) => {
        if (event.target === event.currentTarget) handleDismiss();
      }}
    >
      <aside
        data-advertise-prompt="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="advertise-prompt-title"
        aria-describedby="advertise-prompt-desc"
        className="relative mb-0 w-full max-w-md max-h-[min(90dvh,36rem)] overflow-y-auto overscroll-contain rounded-t-2xl border border-brand-primary/25 bg-brand-surface-lowest p-5 shadow-float sm:rounded-lg"
        style={{ cursor: "default", touchAction: "manipulation" }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-2 top-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-brand-outline hover:bg-brand-surface-container"
          aria-label="Fechar"
          style={{ touchAction: "manipulation" }}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start gap-3 pr-10">
          <div className="rounded bg-brand-chrome p-2.5 text-white" aria-hidden>
            <Megaphone className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p
              id="advertise-prompt-title"
              className="font-display text-base font-bold text-brand-ink"
            >
              Anuncie no Eusébio News
            </p>
            <p
              id="advertise-prompt-desc"
              className="mt-2 text-sm leading-relaxed text-brand-on-surface-variant"
            >
              Divulgue sua marca ou feche uma parceria com o portal. Preencha o formulário e
              nossa equipe comercial entra em contato.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button
                type="button"
                size="sm"
                className="h-11 rounded"
                style={{ touchAction: "manipulation" }}
                onClick={() => {
                  flushSync(() => {
                    handleDismiss();
                  });
                  navigate("/anuncie");
                }}
              >
                Ir para o formulário
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-11 rounded"
                style={{ touchAction: "manipulation" }}
                onClick={handleDismiss}
              >
                Agora não
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </div>,
    document.body,
  );
}
